import { Component, computed, effect, inject, signal } from '@angular/core';
import { LvrHeading } from '../../../core/components/heading/heading';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { INSTITUTION_PERSON_SERVICE } from '../../../api/services/institution-person.service';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { LvrRedirectNav } from '../../../core/classes/redirect-nav.class';
import { PaginatedResponse } from '../../../shared/pagination/paginated-response';
import { PersonType } from '../../../api/app-types/person-type.app';
import {
	CreateInstitutionContactPoint,
	Institution,
	InstitutionBranch,
	InstitutionPerson,
	UpdateInstitutionPerson,
} from '../../../api/app-types/institution.app';
import { ContactPoint, ContactPointCategory } from '../../../api/app-types/contact-point.app';
import { Gender } from '../../../api/app-types/gender.app';
import { FORM_KEYS } from '../../../shared/storage/form-keys.type';
import { maxStringLength } from '../../../shared/validators/length.validator';
import { ClarificationValue } from '../../../api/app-types/relative.app';
import { Address } from '../../../api/app-types/address.app';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { LvrAutoSaveDirective } from '../../../shared/storage/auto-save.directive';
import { LvrField } from '../../../core/components/field/field';
import { LvrInputDirective } from '../../../core/directives/lvr-input.directive';
import { hasError } from '../../../shared/helpers/has-error.func';
import { MatSelectModule } from '@angular/material/select';
import { ImmediateErrorStateMatcher } from '../../../shared/validators/immediate-error-state-matcher';
import { MatInputModule } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { LvrAppendAsteriskPipe } from '../../../shared/pipes/append-asterisk.pipe';
import { AddressCompletion } from '../../../core/components/address-completion/address-completion';
import { MatIcon } from '@angular/material/icon';
import { LvrDataProtection } from '../../../core/components/data-protection/data-protection';
import { LvrDefaultActions } from '../../../core/components/default-actions/default.actions';
import { FormPersistenceService } from '../../../shared/storage/form-persistence.service';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { LvrRestoreActions } from '../../../shared/storage/restore-actions';
import { MatButtonModule } from '@angular/material/button';
import { LvrContactPoint } from '../../../core/components/contact-point/contact-point';
import { LvrPaddingDirective } from '../../../shared/padding/padding.directive';
import { wrapPromiseWithNotify } from '../../../shared/helpers/notify.func';
import { LvrResponsibility } from './responsibility/responsibility';
import { INSTITUTION_SERVICE } from '../../../api/services/institution.service';
import { LvrButton } from '../../../core/components/lvr-button/lvr-button';
import { hasPermission } from '../../../shared/permissions/permission.func';
import { isContactPointComplete } from '../../../shared/helpers/is-contact-point-complete.func';
import { LvrSearchableSelect } from '../../../core/components/searchable-select/searchable-select';

@Component({
	selector: 'lvr-institution-person-edit',
	imports: [
		LvrHeading,
		TranslocoPipe,
		MatFormFieldModule,
		ReactiveFormsModule,
		LvrAutoSaveDirective,
		LvrField,
		LvrInputDirective,
		MatSelectModule,
		MatInputModule,
		MatTooltip,
		LvrAppendAsteriskPipe,
		AddressCompletion,
		LvrContactPoint,
		LvrPaddingDirective,
		MatIcon,
		LvrDataProtection,
		LvrDefaultActions,
		LvrRestoreActions,
		MatButtonModule,
		LvrResponsibility,
		LvrButton,
		LvrSearchableSelect,
	],
	providers: [
		{
			provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
			useValue: { subscriptSizing: 'dynamic' },
		},
	],
	templateUrl: './institution-person-edit.html',
	styleUrl: './institution-person-edit.scss',
})
export class InstitutionPersonEdit extends LvrRedirectNav {
	private readonly fb = inject(FormBuilder);
	private readonly env = inject(ENVIRONMENT);
	private readonly api = inject(INSTITUTION_PERSON_SERVICE);
	private readonly institutionApi = inject(INSTITUTION_SERVICE);
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly persistentStorage = inject(FormPersistenceService);
	private readonly transloco = inject(TranslocoService);
	private readonly session = inject(SessionManagerService);

	private readonly titleRegex = new RegExp(this.env.titleRegexStr);
	private readonly nameRegex = new RegExp(this.env.nameRegexStr);
	public readonly remarkCtrlMaxLength = this.env.textareaMaxLength;
	public readonly matcher = new ImmediateErrorStateMatcher();

	private readonly institution = this.resolveDataAsSignal<Institution>('institution');
	private readonly person = this.resolveDataAsSignal<InstitutionPerson>('person');
	public readonly contactPointCategoriesPage =
		this.resolveDataAsSignal<PaginatedResponse<ContactPointCategory>>('contactPointCategories');
	public readonly genders = this.resolveDataAsSignal<Gender[]>('genders');
	public readonly contactPointCategories = computed(() => this.contactPointCategoriesPage()?.entities ?? []);
	private readonly personTypeResponse = this.resolveDataAsSignal<PaginatedResponse<PersonType>>('personTypes');
	public readonly personTypes = computed(() => this.personTypeResponse()?.entities ?? []);
	public readonly personTypeLabel = (personType: PersonType): string => personType.name;
	public readonly draftId = computed(() => `${FORM_KEYS.UPDATE_INSTITUTION_PERSON}.${this.person()?.id}`);
	public readonly branches = signal<InstitutionBranch[]>([]);

	public readonly saving = signal(false);
	public readonly canEditInstitutionPersonsPermission = hasPermission(
		this.session.permissions,
		'InstitutionPersons.Edit',
	);
	public readonly form = this.initForm(this.fb);

	public constructor() {
		super();
		effect(() => {
			const p = this.person();
			if (!p) return;

			const clar: ClarificationValue = {
				hasBeenClarified: p.hasBeenClarified ?? false,
				clarifiedAt: p.clarifiedAt ?? undefined,
				typeOfClarification: p.typeOfClarification ?? undefined,
			};

			this.form.controls.clarification.setValue(clar, { emitEvent: false });
		});
		if (this.institution()?.id) {
			this.getBranches();
		}
	}

	private initForm(fb: FormBuilder) {
		const form = fb.group({
			categoryId: this.fb.control<number | undefined>(this.person()?.personType.id, {
				validators: [Validators.required],
			}),
			remark: this.fb.control<string>(this.person()?.remark ?? '', {
				validators: [maxStringLength(this.env.textareaMaxLength)],
			}),
			title: this.fb.control(this.person()?.title ?? '', {
				validators: [Validators.pattern(this.titleRegex), maxStringLength(50)],
			}),
			genderId: this.fb.control<number | undefined>(this.person()?.gender?.id),
			firstName: this.fb.control(this.person()?.firstName, {
				validators: [Validators.pattern(this.nameRegex), maxStringLength(50)],
			}),
			lastName: this.fb.control(this.person()?.name, {
				validators: [Validators.required, Validators.pattern(this.nameRegex), maxStringLength(50)],
			}),
			address: this.fb.control<Address | undefined>(this.person()?.address),
			communicationWays: this.fb.control<ContactPoint[]>(this.person()?.contactPoints ?? [], {
				validators: [Validators.max(5)],
			}),
			clarification: this.fb.control<ClarificationValue>({
				hasBeenClarified: false,
				clarifiedAt: undefined,
				typeOfClarification: undefined,
			}),
			responsibleBranches: this.fb.control<number[]>(this.person()?.responsibleBranches?.map((b) => b.id) ?? []),
		});

		return form;
	}

	public hasError = (control: AbstractControl | null, ...errors: string[]) => hasError(control, ...errors);

	/**
	 * Returns 'true' once the data protection clarification value was already given.
	 */
	public readonly isClarificationFinal = computed(() => {
		const r = this.person();
		return !!(r?.hasBeenClarified && r?.clarifiedAt && r?.typeOfClarification != undefined);
	});

	private async getBranches() {
		const page = await this.institutionApi.getBranchesOfInstitution(this.institution()?.id);
		this.branches.set(page.entities);
	}

	public async cancel() {
		this.persistentStorage.deleteDraft('institution-person', this.draftId());
		await this.navigateAfterFinished(['/desk/institution', this.institution()?.id, 'persons', this.person()?.id]);
	}

	public async save() {
		if (this.saving()) return;

		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		const EMPTY_ADDRESS = {
			streetAndHouseNumber: '',
			postalCodeAndCity: '',
		};

		this.saving.set(true);

		try {
			const value = this.form.value;

			const clar = value.clarification ?? {
				hasBeenClarified: false,
				clarifiedAt: undefined,
				typeOfClarification: undefined,
			};

			const body: UpdateInstitutionPerson = {
				address: value.address === null ? (EMPTY_ADDRESS as Address) : value.address,
				categoryId: value.categoryId ?? 0,
				communicationWays: [],
				firstName: value.firstName ?? '',
				name: value.lastName ?? '',
				genderId: value.genderId ?? undefined,
				remark: value.remark ?? '',
				title: value.title ?? '',
				hasBeenClarified: clar.hasBeenClarified,
				clarifiedAt: clar.clarifiedAt,
				typeOfClarification: clar.typeOfClarification,
				responsibleBranchIds: value.responsibleBranches ?? [],
			};

			const filteredCws = (value.communicationWays ?? []).filter(isContactPointComplete);

			if (filteredCws.length > 0) {
				const additionalCws: CreateInstitutionContactPoint[] = filteredCws.map((cp) => ({
					id: cp.id === -1 ? undefined : cp.id,
					preferredWayOfContact: cp.preferredWayOfContact,
					categoryContactPointId: cp.categoryContactPointId,
					contactValue: cp.contactValue?.trim(),
					remark: cp.remark?.trim(),
				}));

				body.communicationWays.push(...additionalCws);
			}

			await wrapPromiseWithNotify(
				this.api.updatePerson(this.institution()!.id, this.person()!.id, body),
				this.transloco.translate('base.notification_message.institution_person_edited', {
					firstName: this.person()!.firstName,
					name: this.person()!.name,
				}),
				this.ntfy,
				this.transloco.translate('base.notification_message.institution_person_edit_error'),
			);
			await this.cancel();
		} finally {
			this.saving.set(false);
		}
	}

	public get communicationWays(): ContactPoint[] {
		return (this.form.controls.communicationWays.value ?? []) as ContactPoint[];
	}

	public addCommunicationWay() {
		const ctrl = this.form.controls.communicationWays;
		if (!ctrl) return;
		const values = ctrl.value ?? [];
		const cp: ContactPoint = {
			id: -1,
			preferredWayOfContact: false,
			categoryContactPointId: -1,
			contactValue: '',
			remark: '',
			personId: -1,
			personTypeId: -1,
		};
		values.push(cp);
		ctrl?.patchValue(values);
	}

	public removeCommunicationWay(index: number) {
		const ctrl = this.form.controls.communicationWays;
		if (!ctrl) return;

		const values = ctrl.value ?? [];
		if (index !== -1) {
			values.splice(index, 1);
			ctrl?.patchValue(values);
		}
	}

	public updateValues(data: { valid: boolean; value: ContactPoint }, index: number) {
		const ctrl = this.form.get('communicationWays');
		if (!ctrl) return;

		const values = (ctrl.value ?? []) as ContactPoint[];

		if (index >= 0 && index < values.length) {
			Object.assign(values[index], data.value); // Update same item in array
		} else {
			values.push(data.value); // Add if new
		}
	}
}
